import { AnimatePresence } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { type ReactNode, useMemo } from "react"
import { EnergyPassportDetailsCell } from "@/features/energy-passport/ui/detail-tables/components/energy-passport-details-cell.tsx"
import { type IEnergyPassportDetailsStore, useEnergyPassportDetailsStore } from "@/features/energy-passport/ui/detail-tables/model/store.ts"
import type { IEnergyPassportDetailsCell } from "@/features/energy-passport/ui/detail-tables/model/types.ts"
import { calcEnergyPassportDetailsQuartersTotals } from "@/features/energy-passport/ui/main-table/model/lib.ts"
import type { ValueOf } from "@/shared/types/interfaces"

interface ITableEditableParentValue {
  value: string | number
}

interface ITableEditableParentRender {
  render: () => ReactNode
}

export type ITableEditableParentRowCell = ITableEditableParentValue | ITableEditableParentRender

interface IProps {
  storeItem: ValueOf<IEnergyPassportDetailsStore>
  cells: IEnergyPassportDetailsCell[]
}

export function EnergyPassportDetailsQuarterRow({ storeItem, cells }: IProps) {
  // Store
  const { toggleQuarterRow } = useEnergyPassportDetailsStore()

  // Functions
  const totals = useMemo(() => {
    return calcEnergyPassportDetailsQuartersTotals(cells, storeItem.quarters)
  }, [storeItem.quarters])

  const totalsCells: ITableEditableParentRowCell[] = useMemo(
    () =>
      cells.map(cell =>
        typeof cell === "string"
          ? {
              value: totals[cell]
            }
          : {
              render: cell.render
            }
      ),
    [totals]
  )

  return (
    <>
      <tr data-selected={storeItem.expanded} className={`remove-bg mb-3.5 rounded-xl transition hover:bg-gray-50 `}>
        <td className="bg-zinc-50 dark:bg-zinc-700">
          <div className="flex items-center gap-3">
            <button
              onClick={() => toggleQuarterRow(storeItem.type)}
              className="hover:!border-blue-400 flex items-center gap-3 rounded-lg border p-2.5 py-1.5 transition duration-100 hover:*:stroke-blue-500 active:border-blue-600 dark:border-zinc-500"
            >
              Все
              <ChevronDown className={`size-5 transition ${storeItem.expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
        </td>
        {totalsCells.map((cell, index) => (
          <td className="bg-zinc-50 dark:bg-zinc-700" key={index}>
            {"value" in cell ? cell.value : cell.render()}
          </td>
        ))}
      </tr>
      <AnimatePresence initial={false}>
        {storeItem.expanded &&
          storeItem.quarters?.map((quarter, index) => {
            return <EnergyPassportDetailsCell storeItem={storeItem} quarter={quarter} key={index} />
          })}
      </AnimatePresence>
    </>
  )
}
